import {
  User,
  Calendar,
  BookOpen,
  AlertTriangle,
  Target,
  Clock,
  MapPin,
  TrendingUp,
  FileText,
  Mail,
  Phone,
  Hash,
  Briefcase,
  GraduationCap,
  Users,
  Settings,
  Database,
  Type,
  Link,
  CheckCircle,
} from 'lucide-react'
import { formatDateSafely } from './dateUtils'

export type DataType = 'string' | 'number' | 'boolean' | 'date' | 'array' | 'object' | 'null' | 'url' | 'email'

export interface RenderingConfig {
  fieldName: string
  dataType: DataType
  icon?: any
  displayName?: string
  formatter?: (value: any) => string
  component?: string
  priority?: number
  isImportant?: boolean
}

export const getDataType = (value: any): DataType => {
  if (value === null || value === undefined) return 'null'
  if (Array.isArray(value)) return 'array'
  if (typeof value === 'object') return 'object'
  if (typeof value === 'boolean') return 'boolean'
  if (typeof value === 'number') return 'number'
  if (typeof value === 'string') {
    if (value.match(/^\d{4}-\d{2}-\d{2}/) || value.match(/\d{1,2}\/\d{1,2}\/\d{4}/)) {
      return 'date'
    }
    if (value.match(/^https?:\/\//)) return 'url'
    if (value.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) return 'email'
    return 'string'
  }
  return 'string'
}

export const getSemanticConfig = (fieldName: string, value: any): RenderingConfig => {
  const lowerFieldName = fieldName.toLowerCase()
  const dataType = getDataType(value)
  
  // Educational/IEP specific field recognition
  if (lowerFieldName.includes('student') || lowerFieldName.includes('name')) {
    return { fieldName, dataType, icon: User, displayName: 'Student Information', isImportant: true, priority: 1 }
  }
  
  if (lowerFieldName.includes('disability') || lowerFieldName.includes('disabilities')) {
    return { fieldName, dataType, icon: AlertTriangle, displayName: 'Disabilities', isImportant: true, priority: 2 }
  }
  
  if (lowerFieldName.includes('goal') || lowerFieldName.includes('goals')) {
    return { fieldName, dataType, icon: Target, displayName: 'Goals', isImportant: true, priority: 3, component: 'goalList' }
  }
  
  if (lowerFieldName.includes('service') || lowerFieldName.includes('services')) {
    return { fieldName, dataType, icon: Briefcase, displayName: 'Services', isImportant: true, priority: 4 }
  }
  
  if (lowerFieldName.includes('school') || lowerFieldName.includes('education')) {
    return { fieldName, dataType, icon: GraduationCap, displayName: 'School Information', priority: 5 }
  }
  
  // Date fields
  if (lowerFieldName.includes('date') || lowerFieldName.includes('birth') || dataType === 'date') {
    return { 
      fieldName, 
      dataType, 
      icon: Calendar, 
      displayName: 'Date Information',
      formatter: (value) => formatDateSafely(value, { format: 'long' })
    }
  }
  
  // Grade and academic info
  if (lowerFieldName.includes('grade') || lowerFieldName.includes('level')) {
    return { fieldName, dataType, icon: BookOpen, displayName: 'Academic Level' }
  }
  
  // Location fields
  if (lowerFieldName.includes('location') || lowerFieldName.includes('address') || lowerFieldName.includes('room')) {
    return { fieldName, dataType, icon: MapPin, displayName: 'Location' }
  }
  
  // Time and frequency
  if (lowerFieldName.includes('time') || lowerFieldName.includes('frequency') || lowerFieldName.includes('duration')) {
    return { fieldName, dataType, icon: Clock, displayName: 'Timing' }
  }
  
  // Assessment and measurement
  if (lowerFieldName.includes('assessment') || lowerFieldName.includes('measurement') || lowerFieldName.includes('baseline') || lowerFieldName.includes('target')) {
    return { fieldName, dataType, icon: TrendingUp, displayName: 'Assessment Data' }
  }
  
  // Provider and personnel
  if (lowerFieldName.includes('provider') || lowerFieldName.includes('teacher') || lowerFieldName.includes('therapist')) {
    return { fieldName, dataType, icon: Users, displayName: 'Personnel' }
  }
  
  // Contact information
  if (lowerFieldName.includes('email') || dataType === 'email') {
    return { fieldName, dataType, icon: Mail, displayName: 'Email', component: 'email' }
  }
  
  if (lowerFieldName.includes('phone') || lowerFieldName.includes('tel')) {
    return { fieldName, dataType, icon: Phone, displayName: 'Phone' }
  }
  
  // URLs and links
  if (dataType === 'url') {
    return { fieldName, dataType, icon: Link, displayName: 'Link', component: 'url' }
  }
  
  // IDs and codes
  if (lowerFieldName.includes('id') || lowerFieldName.includes('code') || lowerFieldName.includes('number')) {
    return { fieldName, dataType, icon: Hash, displayName: 'Identifier' }
  }
  
  // Status and progress
  if (lowerFieldName.includes('status') || lowerFieldName.includes('progress')) {
    return { fieldName, dataType, icon: CheckCircle, displayName: 'Status' }
  }
  
  // Descriptions and notes
  if (lowerFieldName.includes('description') || lowerFieldName.includes('note') || lowerFieldName.includes('comment')) {
    return { fieldName, dataType, icon: FileText, displayName: 'Description', component: 'longText' }
  }
  
  // Page references
  if (lowerFieldName.includes('page') || lowerFieldName.includes('source')) {
    return { fieldName, dataType, icon: FileText, displayName: 'Source Reference', component: 'pageRef' }
  }
  
  // Default fallback
  return {
    fieldName,
    dataType,
    icon: dataType === 'array' ? Database : dataType === 'object' ? Settings : Type,
    displayName: fieldName.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim(),
  }
}

export const formatDisplayName = (fieldName: string): string => {
  return fieldName
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
    .trim()
}

export const shouldGroupArrayItems = (array: any[]): boolean => {
  if (!array || array.length === 0) return false
  if (array.length === 1) return false
  
  // Check if all items have similar structure (same keys)
  if (typeof array[0] === 'object' && array[0] !== null) {
    const firstKeys = Object.keys(array[0]).sort()
    return array.every(item => {
      if (typeof item !== 'object' || item === null) return false
      const itemKeys = Object.keys(item).sort()
      return JSON.stringify(firstKeys) === JSON.stringify(itemKeys)
    })
  }
  
  return false
}

export const getPriorityOrder = (configs: RenderingConfig[]): RenderingConfig[] => {
  return configs.sort((a, b) => {
    if (a.isImportant && !b.isImportant) return -1
    if (!a.isImportant && b.isImportant) return 1
    if (a.priority && b.priority) return a.priority - b.priority
    if (a.priority && !b.priority) return -1
    if (!a.priority && b.priority) return 1
    return a.fieldName.localeCompare(b.fieldName)
  })
}